import { useState } from 'react'
import { Plus } from 'lucide-react'
import { Alert, Button, Dialog, Field, Menu, SegmentedControl, Select } from '@/components/common'
import { PreviewAudienceControl } from './PreviewAudienceControl'
import { availableLeagueHubs } from './nativeHubModel.js'
import { homeScreen } from './nativeScreens'
import { PREVIEW_CATALOG } from './nativePreviewModel'
import { TargetPicker } from './experienceEditorControls'
import { GAME_STATES, addGamePage, exampleGames, gamePageLabel, gamePageScopeIssue, gamePageState, selectedGamePage, sortedGamePages } from './gamePageModel'
import styles from './nativeWorkspace.module.css'

const DEVICES = [{ value: 'ios', label: 'iPhone' }, { value: 'android', label: 'Android' }]
const NEW_VARIATION = 'new'

// One row under the page title, the same on every page: what is being edited
// on the left; how the preview is rendered on the right, with the device last
// so it never moves when the page-specific controls change.
export function NativeWorkspaceToolbar({ workspace, surface, device, setDevice, disabled }) {
  return <div className={styles.toolbar}>
    <EditingTarget workspace={workspace} surface={surface} disabled={disabled} />
    <div className={styles.toolbarPreview}>
      {surface === 'events' ? <GamePreviewControls workspace={workspace} /> : <PreviewAudienceControl workspace={workspace} />}
      <SegmentedControl label="Preview device" items={DEVICES} value={device} onValueChange={setDevice} />
    </div>
  </div>
}

// Every page an operator can change is in one list: Home and league hubs,
// Home variations, and game pages. Add creates any of them.
function EditingTarget({ workspace, surface, disabled }) {
  const [adding, setAdding] = useState(null)
  const screens = workspace.draft.screens
  const home = homeScreen(workspace.draft)
  const experience = workspace.experience
  const scenes = experience.home_scenes ?? []
  const pages = sortedGamePages(experience)
  const hubChoices = availableLeagueHubs(workspace.draft, workspace.baseCatalog)
  const creatingVariation = surface === 'scenes' && !workspace.editorSelection.sceneId
  const value = surface === 'events' ? selectedGamePage(experience, workspace.editorSelection).id
    : surface === 'scenes' ? workspace.editorSelection.sceneId ?? NEW_VARIATION : workspace.screen.id

  const select = (next) => {
    if (next === NEW_VARIATION) return
    if (screens.some((screen) => screen.id === next)) { workspace.selectScreen(next); return }
    if (pages.some((page) => page.id === next)) { workspace.setEditorSelection({ surface: 'events', templateId: next, eventId: null, section: null }); return }
    workspace.setEditorSelection({ surface: 'scenes', sceneId: next, screenId: home.id })
  }

  return <div className={styles.toolbarTarget}>
    <label className={styles.toolbarField}>
      <span>Editing</span>
      <Select size="compact" value={value} onChange={(event) => select(event.target.value)}>
        <optgroup label="Pages">
          {screens.map((screen) => <option key={screen.id} value={screen.id}>{screen.context ? screen.title : 'Home'}</option>)}
        </optgroup>
        {scenes.length || creatingVariation ? <optgroup label="Home variations">
          {scenes.map((scene) => <option key={scene.id} value={scene.id}>{scene.name}</option>)}
          {creatingVariation ? <option value={NEW_VARIATION}>New variation</option> : null}
        </optgroup> : null}
        <optgroup label="Game pages">
          {pages.map((page) => <option key={page.id} value={page.id}>{gamePageLabel(page)}</option>)}
        </optgroup>
      </Select>
    </label>
    <Menu
      triggerLabel="Add"
      align="start"
      trigger={<Button size="compact" variant="secondary" disabled={disabled}><Plus size={14} aria-hidden="true" />Add</Button>}
      items={[
        { key: 'hub', label: 'League hub', disabled: !hubChoices.length || screens.length >= 16, onSelect: () => setAdding('hub') },
        { key: 'variation', label: 'Home variation', disabled: scenes.length >= 16, onSelect: () => workspace.setEditorSelection({ surface: 'scenes', sceneId: null, screenId: home.id }) },
        { key: 'game', label: 'Game page', disabled: experience.event_templates.length >= 32, onSelect: () => setAdding('game') },
      ]}
    />
    {adding === 'hub' ? <AddLeagueHubDialog workspace={workspace} choices={hubChoices} disabled={disabled} onClose={() => setAdding(null)} /> : null}
    {adding === 'game' ? <AddGamePageDialog workspace={workspace} disabled={disabled} onClose={() => setAdding(null)} /> : null}
  </div>
}

function AddLeagueHubDialog({ workspace, choices, disabled, onClose }) {
  const [choiceKey, setChoiceKey] = useState('')
  const [error, setError] = useState('')
  const hubKey = (choice) => `${choice.sport}:${choice.id}`
  const choice = choices.find((item) => hubKey(item) === choiceKey) ?? choices[0]
  const create = () => {
    try { workspace.createLeagueHub(choice); onClose() }
    catch (failure) { setError(failure.message) }
  }
  return <Dialog open title="Add league hub" onClose={onClose} footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button disabled={disabled || !choice} onClick={create}>Create hub</Button></>}>
    <Field label="League"><Select value={choice ? hubKey(choice) : ''} onChange={(event) => setChoiceKey(event.target.value)}>{choices.map((item) => <option key={hubKey(item)} value={hubKey(item)}>{item.name}</option>)}</Select></Field>
    <p>Start with games from this league. Add a campaign or pin a matchup in the collection.</p>
    {error ? <Alert variant="error">{error}</Alert> : null}
  </Dialog>
}

function AddGamePageDialog({ workspace, disabled, onClose }) {
  const [kind, setKind] = useState('league')
  const [value, setValue] = useState('')
  const [error, setError] = useState('')
  const issue = value ? gamePageScopeIssue(workspace.experience, { kind, value }) : null
  const create = () => {
    try {
      const result = addGamePage(workspace.experience, { kind, value }, workspace.previewCatalog || PREVIEW_CATALOG)
      workspace.updateExperience(result.experience)
      workspace.setEditorSelection({ surface: 'events', templateId: result.template.id, eventId: null, section: null })
      onClose()
    } catch (failure) { setError(failure.message) }
  }
  return <Dialog open title="Add game page" onClose={onClose} footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button disabled={disabled || !value || Boolean(issue)} onClick={create}>Create page</Button></>}>
    <p>Change how these games look. Everything else follows All games.</p>
    <TargetPicker catalog={workspace.previewCatalog || PREVIEW_CATALOG} kind={kind} value={value} onKindChange={setKind} onChange={setValue} disabled={disabled} />
    {issue || error ? <Alert variant="warning">{issue || error}</Alert> : null}
  </Dialog>
}

// A game page previews one of its games, before start or live. The phone keeps
// its place: these replace View as in the same slot.
function GamePreviewControls({ workspace }) {
  const selection = workspace.editorSelection
  const state = gamePageState(selection)
  const template = selectedGamePage(workspace.experience, selection)
  const games = exampleGames(template, workspace.previewCatalog || PREVIEW_CATALOG, state)
  const event = selection.eventId ? games.find((item) => item.id === selection.eventId) : games[0]
  return <>
    <label className={`${styles.toolbarField} ${styles.gameField}`}>
      <span>Game</span>
      <Select size="compact" value={event?.id ?? ''} onChange={(change) => workspace.setEditorSelection({ eventId: change.target.value })}>
        {!event ? <option value="">{selection.eventId ? 'Paired game isn’t in this data' : 'No games for this page'}</option> : null}
        {games.map((item) => <option key={item.id} value={item.id}>{item.live ? `${item.name} · Live` : item.name}</option>)}
      </Select>
    </label>
    <SegmentedControl label="Game state" items={GAME_STATES} value={state} onValueChange={(next) => workspace.setEditorSelection({ phase: next, layer: next === 'in_play' ? 'in_play' : 'base', eventId: null })} />
  </>
}
